import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import client from '../../api/client'
import { useAuthStore } from '../../store/authStore'
import Spinner from '../../components/Spinner'


type CardInfo = {
  card_type_name: string
  card_type_desc: string
  day_gan: string
  element: string
  card_image_path?: string
  card_version?: string
  share_token?: string
}

export default function CharacterCard() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const [card, setCard] = useState<CardInfo | null>(null)
  const [loading, setLoading] = useState(false)
  const [copied, setCopied] = useState(false)
  const [version, setVersion] = useState<string>('v1')

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return }
    setLoading(true)
    client.get('/features/character-card')
      .then((r) => {
        setCard(r.data)
        setVersion(r.data.card_version || 'v1')
      })
      .catch((err) => {
        console.error('[CharacterCard] Error:', err)
      })
      .finally(() => setLoading(false))
  }, [])

  const handleVersionChange = (newVersion: string) => {
    if (version === newVersion) return
    setLoading(true)
    client.post('/features/character-card/version', { version: newVersion })
      .then((r) => {
        setVersion(newVersion)
        if (r.data.card_image_path) {
          setCard(prev => prev ? { ...prev, card_image_path: r.data.card_image_path, card_version: newVersion } : null)
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  const copyLink = async () => {
    if (!card?.share_token) return
    const url = `${window.location.origin}/api/share/${card.share_token}`
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      const el = document.createElement('textarea')
      el.value = url
      el.style.position = 'fixed'
      el.style.opacity = '0'
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg)] p-4 sm:p-6">
      <div className="max-w-md mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/dashboard')} className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-bold text-[var(--color-text-primary)]">🎴 내 캐릭터 카드</h1>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Spinner size="lg" /></div>
        ) : card ? (
          <div className="space-y-4">
            {/* 카드 이미지 */}
            <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-3xl overflow-hidden aspect-[3/4]">
              {card.card_image_path ? (
                <img
                  src={card.card_image_path}
                  alt="캐릭터"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[var(--color-error)] text-sm">이미지 경로 없음</div>
              )}
            </div>

            {/* 버전 선택 */}
            <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-4">
              <p className="text-[var(--color-text-tertiary)] text-xs mb-3">캐릭터 카드 버전</p>
              <div className="flex gap-3">
                <button
                  onClick={() => handleVersionChange('v1')}
                  className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors border ${version === 'v1'
                    ? 'bg-[var(--color-accent)] text-[var(--color-primary)] border-[var(--color-accent)]'
                    : 'bg-[var(--color-bg)] text-[var(--color-text-tertiary)] border-[var(--color-border-default)] hover:border-[var(--color-border-accent)]'}`}
                >
                  v1
                </button>
                <button
                  onClick={() => handleVersionChange('v2')}
                  className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors border ${version === 'v2'
                    ? 'bg-[var(--color-accent)] text-[var(--color-primary)] border-[var(--color-accent)]'
                    : 'bg-[var(--color-bg)] text-[var(--color-text-tertiary)] border-[var(--color-border-default)] hover:border-[var(--color-border-accent)]'}`}
                >
                  v2
                </button>
              </div>
            </div>

            {/* 공유 버튼 */}
            {card.share_token && (
              <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5">
                <p className="text-[var(--color-text-tertiary)] text-sm mb-3">공유하기</p>
                <div className="flex gap-2">
                  <button onClick={copyLink}
                    className="flex-1 py-2.5 border border-[var(--color-border-default)] text-[var(--color-text-tertiary)] rounded-xl text-sm hover:text-[var(--color-text-primary)] hover:border-[var(--color-border-accent)] transition-colors">
                    {copied ? '✓ 복사됨' : '🔗 링크 복사'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center text-[var(--color-text-tertiary)] py-20">
            <p className="text-4xl mb-4">🎴</p>
            <p>캐릭터 카드를 불러올 수 없습니다.</p>
          </div>
        )}
      </div>
    </div>
  )
}
